import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Button, Form, Input, Select, Table, Row, Col, Typography, App } from 'antd';
import { ArrowLeftOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import api from '@/services/api.service';
import { supplierDebitNoteService } from '@/services/supplierDebitNote.service';

const { Title } = Typography;

function unwrap<T>(res: any): T {
  const data = res?.data ?? res;
  return (data?.data ?? data) as T;
}

export default function CreateDebitNotePage() {
  const { t } = useTranslation('finance');
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [lines, setLines] = useState<Array<{ id: string; description: string; amount: number }>>([
    { id: '1', description: '', amount: 0 },
  ]);

  const { data: suppliers = [] } = useQuery({
    queryKey: ['suppliers'],
    queryFn: async () => {
      const res = await api.get('/procurement/suppliers');
      const raw = unwrap<{ id: string; supplier_name?: string }[]>(res);
      return Array.isArray(raw) ? raw : [];
    },
  });

  const createMutation = useMutation({
    mutationFn: (payload: {
      debit_note_date: string;
      supplier_id: string;
      supplier_invoice_id?: string;
      debit_note_type?: string;
      remarks?: string;
      lines: Array<{ description?: string; amount: number; tax_amount?: number; reason?: string }>;
    }) => supplierDebitNoteService.create(payload),
    onSuccess: (data) => {
      message.success(t('debitNoteCreated'));
      queryClient.invalidateQueries({ queryKey: ['supplier-debit-notes'] });
      navigate(`/fin/debit-notes/${data.id}`);
    },
    onError: (e: any) => {
      message.error(e?.response?.data?.message || t('errCreateDebitNote'));
    },
  });

  const addLine = () => {
    setLines((prev) => [...prev, { id: String(Date.now()), description: '', amount: 0 }]);
  };

  const removeLine = (id: string) => {
    if (lines.length <= 1) return;
    setLines((prev) => prev.filter((l) => l.id !== id));
  };

  const updateLine = (id: string, field: 'description' | 'amount', value: string | number) => {
    setLines((prev) =>
      prev.map((l) => (l.id === id ? { ...l, [field]: field === 'amount' ? Number(value) || 0 : value } : l))
    );
  };

  const onFinish = () => {
    const values = form.getFieldsValue();
    const validLines = lines
      .map((l) => ({ description: l.description || undefined, amount: Number(l.amount) || 0 }))
      .filter((l) => l.amount > 0);
    if (validLines.length === 0) {
      message.error(t('addAtLeastOneLineAmount'));
      return;
    }
    createMutation.mutate({
      debit_note_date: values.debit_note_date || dayjs().format('YYYY-MM-DD'),
      supplier_id: values.supplier_id,
      supplier_invoice_id: values.supplier_invoice_id || undefined,
      debit_note_type: values.debit_note_type || 'CommercialAdjustment',
      remarks: values.remarks || undefined,
      lines: validLines,
    });
  };

  return (
    <div style={{ padding: 24 }}>
      <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/fin/debit-notes')} style={{ marginBottom: 16 }}>
        {t('backToDebitNotes')}
      </Button>

      <Title level={4} style={{ marginBottom: 24 }}>
        {t('createDebitNoteStandalone')}
      </Title>

      <Form form={form} layout="vertical" initialValues={{ debit_note_date: dayjs().format('YYYY-MM-DD'), debit_note_type: 'CommercialAdjustment' }}>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item name="debit_note_date" label={t('date')} rules={[{ required: true }]}>
              <Input type="date" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="supplier_id" label={t('supplier')} rules={[{ required: true }]}>
              <Select
                placeholder={t('selectSupplier')}
                showSearch
                optionFilterProp="label"
                options={suppliers.map((s) => ({ value: s.id, label: s.supplier_name || s.id }))}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="debit_note_type" label={t('type')}>
              <Select
                options={[
                  { value: 'CommercialAdjustment', label: t('debitNoteTypeCommercialAdjustment') },
                  { value: 'RateDifference', label: t('debitNoteTypeRateDifference') },
                  { value: 'Other', label: t('debitNoteTypeOther') },
                ]}
              />
            </Form.Item>
          </Col>
          <Col span={16}>
            <Form.Item name="remarks" label={t('remarks')}>
              <Input.TextArea rows={2} placeholder={t('remarks')} />
            </Form.Item>
          </Col>
        </Row>

        <Card title={t('lines')} extra={<Button type="dashed" icon={<PlusOutlined />} onClick={addLine}>{t('addLine')}</Button>} style={{ marginBottom: 16 }}>
          <Table
            size="small"
            dataSource={lines}
            rowKey="id"
            pagination={false}
            columns={[
              { title: t('description'), dataIndex: 'description', key: 'description', render: (_, r) => <Input value={r.description} onChange={(e) => updateLine(r.id, 'description', e.target.value)} placeholder={t('description')} /> },
              { title: t('amount'), dataIndex: 'amount', key: 'amount', width: 140, render: (_, r) => <Input type="number" min={0} step={0.01} value={r.amount || ''} onChange={(e) => updateLine(r.id, 'amount', e.target.value)} placeholder="0" /> },
              { title: '', key: 'action', width: 50, render: (_, r) => (lines.length > 1 ? <Button type="text" danger icon={<DeleteOutlined />} onClick={() => removeLine(r.id)} /> : null) },
            ]}
          />
        </Card>

        <Button type="primary" onClick={onFinish} loading={createMutation.isPending}>
          {t('createDebitNote')}
        </Button>
      </Form>
    </div>
  );
}
